import Script from 'next/script';
import React, { useEffect, useRef, useState } from 'react';

import ChevronLeftSmall from '@/assets/icons/chevron-left-small.svg';
import MinusIcon from '@/assets/icons/minus-normal.svg';
import PlusIcon from '@/assets/icons/plus-normal.svg';

import VisioWebEssential from '../../../public/visioweb/visioweb.essential';
import {
  extractQueryParams,
  isServiceTile,
} from '@/helpers/floor-plan/floor-plan-helpers';
import { isBrowser } from '@/helpers/is-browser';
import {
  brandGrey2,
  neutralGrey300,
  neutralGrey400,
} from '@/helpers/tailwindColors';
import { clearUrlParams } from '@/helpers/utils';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { FloorPlanFilter } from '../floor-plan-filter/floor-plan-filter';
import Carousel from '../ui/carousel/carousel';
import { FloorPlanInfoBubble } from './floor-place-info-bubble';
import {
  ActiveBuilding,
  Essential,
  FilterTile,
  Floor,
  FloorPlanMapProps,
  Instruction,
  MapTile,
  Position,
  QueryParams,
} from './floor-plan-map.interface';
import { navigationTranslator } from './navigation-translator';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useUser } from '@/providers/UserProvider';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';

export const FloorPlanMap = ({
  mapHash,
  node_locale,
  activeTiles,
  listStyle,
  performListFiltering,
  performCategoryListing,
  updateFilterList,
  setClickHistory,
  clickHistory,
  setMarkedPlaces,
  allTiles,
  homepage,
}: FloorPlanMapProps) => {
  const searchParams = useSearchParams();
  const isLivliFamily = useIsMPLivliFamily();
  const router = useRouter();
  const pathname = usePathname();
  const [essential, setEssential] = useState<Essential>();
  const [activeBuilding, setActiveBuilding] = useState<ActiveBuilding>();
  const [activeFloorId, setActiveFloorId] = useState<string>('');
  const [activeStore, setActiveStore] = useState<MapTile>();
  const [paramLoaded, setParamLoaded] = useState<boolean>(false);
  const [serviceData, setServiceData] = useState<{ [key: string]: FilterTile }>(
    {},
  );
  const [mapIsEnriched, setMapIsEnriched] = useState(false);
  const [instruction, setInstruction] = useState<Array<Instruction>>([]);
  const [selectedShopRouteDetails, setSelectedShopRouteDetails] = useState<{
    shopName: string;
    routePreference: string;
  }>({
    shopName: '',
    routePreference: '',
  });

  const floorPlanMapContainer = useRef<HTMLDivElement>(null);
  const bubbleElement = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    kioskId: '',
    routePreference: '',
    destination: '',
  });

  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const currentRouteRef = useRef<any>(null);

  const { user } = useUser();

  //Ids of all places from Contentful
  const floorIdsFromContentful = allTiles
    .flatMap((tile) => [...(tile.floorPlanIDs?.flat() ?? []), ...(tile?.unitId ?? [])])
    .reduce((acc: string[], curr) => {
      if (curr && !acc.includes(curr)) {
        acc.push(curr);
      }
      return acc;
    }, []);

  //////////////////////
  // SETTINGS
  /////////////////////
  // TODO these colors are subject to change
  const __filterIcon = '/visioweb/images/service-map-icon.svg';
  const __activeIcon = '/visioweb/images/service-map-selected-icon.svg';
  const __filterColor = neutralGrey300;
  const __activeColor = neutralGrey400;
  const __defaultTileColor = brandGrey2;
  const __imagePath = '/visioweb/images';

  const __defaultZoomRadius = 100;
  const __singleStoreZoomRadius = 300;
  const __zoomSpeed = 0.5;
  const __intialZoomRadius = 700;

  let businessUnitCode = '';

  if (homepage && 'meetingPlace' in homepage) {
    const meetingPlace = homepage.meetingPlace;
    businessUnitCode = meetingPlace.businessUnitCode;
  }

  let tagId = '';
  if (homepage && 'reservedTagId' in homepage) {
    tagId = homepage.reservedTagId?.replace('meetingPlace', '') ?? '';
  }

  ///////////////////////
  // EVENTS
  //////////////////////

  // Initialize tha VisioWeb Map

  const getVisioWebScriptUrl = () => {
    // const hasSharedDomain =
    //   config.GATSBY_HAS_SHARED_DOMAIN &&
    //   config.GATSBY_HAS_SHARED_DOMAIN === 'true'
    //     ? true
    //     : false; //Does the site reside on the same domain as other sites?

    // const sharedDomainAndAssetPrefix =
    //   hasSharedDomain &&
    //   config.GATSBY_SITE_PATH_PREFIX &&
    //   config.GATSBY_SITE_PATH_PREFIX !== ''
    //     ? true
    //     : false; //If the site reside on same site as other sites, and has a site prefix set

    const baseUrl = '/visioweb/visioweb.js';
    // const vwUrl = sharedDomainAndAssetPrefix
    //   ? `/${config.GATSBY_SITE_PATH_PREFIX}${baseUrl}`
    //   : baseUrl;
    const vwUrl = baseUrl;

    return vwUrl;
  };

  const initVisioWebScript = async () => {
    if (!floorPlanMapContainer.current || !window.visioweb) return;

    const e = new VisioWebEssential({
      element: floorPlanMapContainer.current,
      imagePath: __imagePath,
    });

    e.setParameters({
      parameters: {
        baseURL: 'https://mapserver.visioglobe.com/',
        hash: mapHash,
        mapview: {
          config: { cameratype: 'perspective', viewtype: 'multibuilding' },
          colors: { active: __activeColor },
        },
        parameters: {
          locale: { language: node_locale },
        },
      },
    });

    await e.createMapviewer();

    const defaultBuilding =
      e.venue.layout.buildings[e.venue.layout.defaultBuildingIndex];
    e.venue.goToBuilding({ id: defaultBuilding.id });

    // set min and max radius to the min and max altitudes respectively
    e._mapviewer.camera.minRadius = e._mapviewer.camera.minAltitude;
    e._mapviewer.camera.maxRadius = e._mapviewer.camera.maxAltitude;

    setEssential(e as unknown as Essential);
  };

  useEffect(() => {
    if (window.visioweb) {
      initVisioWebScript();
    }
  }, []);

  useEffect(() => {
    const params = extractQueryParams(searchParams);
    setQueryParams(params);
  }, [searchParams]);

  useEffect(() => {
    return () => {
      //Stop events and destroy map when leaving the page
      if (essential) {
        essential.off('exploreStateWillChange');
        essential.off('navigationComputed');
        essential.destroyMapviewer();
      }
    };
  }, [essential]);

  // Handle params to active tile
  useEffect(() => {
    if (!essential) return;

    const handleUrl = () => {
      if (!isBrowser || activeFloorId === '' || paramLoaded) return;

      const paramsID = new URL(location.href).searchParams.get('id');
      if (paramsID) {
        const match = allTiles.find(
          (tile) => tile.contentfulId?.toLocaleLowerCase() === paramsID,
        );
        if (match) {
          filterClickOnTenant(match);
          setParamLoaded(true);
          return;
        }
      }

      const fpActiveid =
        searchParams.get('fpActive') ||
        new URL(location.href).searchParams.get('fpActive');
      if (fpActiveid && essential) {
        const match = allTiles.find((tile) =>
          tile.floorPlanIDs?.includes(fpActiveid) || tile?.unitId?.includes(fpActiveid),
        );

        if (match && essential.content.places[fpActiveid]) {
          filterClickOnTenant(match, fpActiveid);
          setParamLoaded(true);
        }
      }
    };
    handleUrl();

    if (!activeFloorId) {
      const initActiveBuilding = essential.venue?.layout?.buildings?.filter(
        (building: ActiveBuilding) =>
          building.id === essential.venue.currentBuildingID,
      )[0];

      if (initActiveBuilding) {
        setActiveBuilding(initActiveBuilding);
        const initActiveFloor = initActiveBuilding.floors.filter(
          (floor: Floor) => floor.levelIndex === 0,
        );

        if (initActiveFloor.length > 0) {
          setActiveFloorId(() => initActiveFloor[0].id);
        } else {
          setActiveFloorId(() => initActiveBuilding.floors[0].id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFloorId, essential]);

  // Set Icons for active tiles
  useEffect(() => {
    if (!essential) return;

    clearAllMarkedPlaces();

    const allPlaces = essential.content.places;
    const newMarkedPlaces: FilterTile[] = [];

    for (const tile of activeTiles) {
      const tileIsValid =
        (tile.floorPlanIDs?.some((id) => {
          if (id != null && allPlaces[id] != null) {
            return true;
          }
          return false;
        }) ?? false) ||
        (tile?.unitId?.some((id) => id != null && allPlaces[id] != null) ?? false);

      if (!tileIsValid) {
        continue;
      }

      for (const id of [...(tile.floorPlanIDs ?? []), ...(tile?.unitId ?? [])]) {
        if (
          activeStore?.id === id ||
          activeStore?.ids?.includes(id) ||
          !Object.keys(allPlaces).includes(id) ||
          allPlaces[id] == undefined
        ) {
          continue;
        }
        try {
          // Set icon and colors hover/ effect
          if (isServiceTile(tile.type)) {
            setOrResetServiceIcon(id, __filterIcon, true);
          } else if (!isServiceTile(tile.type)) {
            essential.content.setPlaceColor({
              id,
              color: __filterColor,
            });

            essential.content.setPlaceIcon({
              id,
              url: { ...tile.images, slug: tile.slug },
            });
          }
        } catch {
          /* Deliberatly empty comment to satisfy try catch block, unable to set placeColor or placeIcon - place is a POI 🥸 */
        }

        if (
          !newMarkedPlaces.flatMap((place) => [...(place.floorPlanIDs ?? []), ...(place.unitId ?? [])]).includes(id)
        ) {
          newMarkedPlaces.push(tile);
        }
      }
    }
    setMarkedPlaces(newMarkedPlaces);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTiles]);

  //When activeStore updates...
  useEffect(() => {
    if (!essential) return;
    if (!activeStore || !activeStore.ids) {
      return;
    }

    const allPlaces = essential.content.places;

    clearAllMarkedPlaces(activeStore.id);

    if (activeStore?.ids?.length === 1) {
      essential.content.setActivePlace({
        place: activeStore.ids[0],
      });
    } else if (activeStore.floor) {
      goToFloor(activeStore.floor);
    }

    for (const id of activeStore.ids) {
      const isActiveStore =
        activeStore?.ids?.includes(id) || activeStore?.id === id;

      const allMatchingAreService = allTiles
        .filter((item) => item.floorPlanIDs?.includes(id))
        .every((item) => item.type == 'MeetingPlacePageService');

      if (!Object.keys(allPlaces).includes(id)) continue;

      if (
        checkThatIdIsInValidAndService(id) &&
        !activeStore.icon?.shopTenant &&
        allMatchingAreService
      ) {
        //It's a service! 🥳
        const iconUrl = isActiveStore ? __activeIcon : __filterIcon;
        setOrResetServiceIcon(id, iconUrl, true);
      } else {
        essential.content.setPlaceColor({
          id,
          color: isActiveStore ? __activeColor : __filterColor,
        });
      }
    }

    if (
      isBrowser &&
      activeStore &&
      activeStore.ids &&
      activeStore.ids.length > 0
    ) {
      const params = new URLSearchParams(searchParams.toString());

      const activeId = activeStore.ids[0];
      const currentFpActive = params.get('fpActive');
      if (activeId && currentFpActive !== activeId) {
        params.set('fpActive', activeId);
        const newUrl = `${pathname}?${params.toString()}`;
        router.replace(newUrl, { scroll: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore, essential, pathname, router, searchParams]);

  useEffect(() => {
    if (!essential || mapIsEnriched) return;

    const allPlaces = essential.content.places;
    essential.content.placeBubbleEnabled = true;
    const newServiceData: { [key: string]: FilterTile } = {};

    for (const key of Object.keys(allPlaces)) {
      try {
        essential.content.setPlaceContent({
          ...allPlaces[key],
          name: '',
        });
      } catch {
        /* Deliberatly empty comment to satisfy try catch block, unable to set placeContent - place is a POI 🥸 */
      }
    }

    for (const tile of allTiles) {
      //For all tiles
      const floorPlanIds = [...(tile.floorPlanIDs ?? []), ...(tile?.unitId ?? [])];
      for (const id of floorPlanIds) {
        //For the current tile's id:s

        if (
          !Object.keys(allPlaces).includes(id) ||
          !floorIdsFromContentful.includes(id)
        ) {
          continue;
        }

        const floor = allPlaces[id].floor;
        const name = tile.title ? tile.title : '';

        if (!isServiceTile(tile.type)) {
          essential.content.setPlaceContent({
            id,
            name,
            description: tile.title,
            categories: tile.categories,
            url: tile.slug,
            floor,
          });
          essential.content.setPlaceIcon({
            id,
            url: { ...tile.images, slug: tile.slug, shopTenant: true },
          });
        } else if (isServiceTile(tile.type)) {
          if (!Object.keys(newServiceData).includes(id)) {
            newServiceData[id] = { ...tile, title: name };
          }
        }
      }
    }
    setServiceData(newServiceData);
    setMapIsEnriched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [essential, allTiles, mapIsEnriched]);

  useEffect(() => {
    if (!essential) return;
    essential._mapviewer
      .computeRoute({
        src: queryParams.kioskId,
        dst: queryParams.destination,

        computeNavigation: true,
        language: node_locale,
        routingParameters: {
          excludedAttributes:
            queryParams.routePreference === 'no-lift'
              ? ['lift']
              : queryParams.routePreference === 'lift-only'
                ? ['stairway', 'escalator', 'stair', 'notPRM']
                : [],
        },
      })
      .then(function (res) {
        if (res) {
          essential.route.computeNavigation = true;
          const createdRoute = new window.visioweb.Route(
            essential._mapviewer,
            res?.data || null,
            {
              imagePath: __imagePath,
              pathOpacity: 0.75,
              arrowWidth: 4,
              pathWidth: 1.75,
              pathColor: {
                past: '#000',
                current: '#000',
                future: '#000',
              },
            },
          );

          const findDestination = allTiles.find((tile) =>
            tile.floorPlanIDs?.includes(queryParams?.destination) || tile?.unitId?.includes(queryParams?.destination),
          );

          const translations: any = navigationTranslator(node_locale);

          if (findDestination) {
            setSelectedShopRouteDetails({
              shopName: findDestination?.title,
              routePreference:
                queryParams?.routePreference == 'shortest'
                  ? translations?.internal?.Shortest
                  : queryParams?.routePreference == 'no-lift'
                    ? translations?.internal['No elevator']
                    : translations?.internal['Accessible'],
            });
          }

          const allPlaces = essential.content.places;

          for (const id of Object.keys(allPlaces)) {
            if (id == queryParams?.kioskId) {
              const kioskFloor = allPlaces[id]?.floor;
              goToFloor(kioskFloor);
            }
          }

          currentRouteRef.current = createdRoute;
          essential!._mapviewer!.setupNavigationTranslator(
            essential.content.places,
          );
          essential!.navigation!.translator =
            essential!._mapviewer!.navigationTranslator;
          const translation = navigationTranslator(node_locale);
          essential!.navigation!.translator.addOrUpdateLanguage(
            node_locale || '',
            JSON.stringify(translation),
          );
          essential!.navigation!.setLanguageCode({ languageCode: node_locale });

          const allFloors =
            essential!._mapviewer!.getExtraData()?.resources[node_locale]
              .localized.locale[node_locale].venueLayout;
          essential!.navigation.translator.setFloors(allFloors);

          essential!.navigation!.languageCode =
            window.visioweb.Utils.getLanguageMatch(
              essential!._parameters!.locale.language,
              essential!.navigation!.translator.getLanguageCodes(),
            );

          essential.navigation._currentNavigation = createdRoute?.navigation;
          essential!.navigation!.translator.translateInstructions(
            res.data.navigation,
            node_locale,
          );
          essential!.navigation.currentInstructionIndex = 0;
          const instructions: Array<Instruction> = [];

          const initActiveBuilding = essential.venue?.layout?.buildings?.filter(
            (building: ActiveBuilding) =>
              building.id === essential.venue.currentBuildingID,
          )[0];
          for (let i = 0; i < essential.navigation.nbInstructions; i++) {
            const instructionData: any =
              essential.navigation._currentNavigation.getInstructionData(i);

            instructions.push({
              ...instructionData,
              instructionIndex: i,
              floors: initActiveBuilding.floors.find(
                (floor) => floor.id == instructionData.dataset,
              ),
            });
          }
          setInstruction(instructions);
        }
        const viewPoint =
          currentRouteRef?.current?.getViewpointPosition('wholeRoute');

        essential?._mapviewer.camera.goTo(viewPoint);
      })
      .catch(function (e) {
        console.log('error value is', e);
      });
  }, [essential]);

  useEffect(() => {
    if (queryParams?.kioskId && isBrowser) {
      dataLayer.push({
        event: 'cross_device_navigation',
        meeting_place: tagId,
        meeting_place_id: businessUnitCode ?? '',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  }, [essential]);

  const checkThatIdIsInValidAndService = (id: string): boolean => {
    if (!essential) return false;

    return (
      Object.keys(serviceData).includes(id) &&
      floorIdsFromContentful.includes(id) &&
      essential?.content.places[id] != undefined
    );
  };

  if (essential) {
    // Mapevent - Click on tenant 🏘️
    essential.onObjectMouseUp = ({ targetElement }) => {
      setActiveStore(() => undefined);
      essential.content.setActivePlace({ place: targetElement });
      const id = essential.content.activePlaceID;
      essential.content.placeBubbleEnabled =
        floorIdsFromContentful.includes(id);

      if (activeStore && !activeStore.ids?.includes(id)) {
        if (checkThatIdIsInValidAndService(activeStore.id)) {
          //Reset service icon
          const serviceActiveTile =
            activeTiles.filter((t) => t.floorPlanIDs?.includes(activeStore.id))
              .length > 0;

          const iconUrl = serviceActiveTile
            ? __filterIcon
            : (serviceData[activeStore.id].images?.icon?.url ?? '');
          setOrResetServiceIcon(activeStore.id, iconUrl);
        }
      }

      const findMatch = allTiles.find((tile) =>
        tile.floorPlanIDs?.includes(id) || tile?.unitId?.includes(id),
      );

      if (findMatch) {
        if (activeStore && !(id && (findMatch.floorPlanIDs?.includes(id) || findMatch?.unitId?.includes(id)))) {
          //Clear
          setActiveStore(undefined);
          clearMarkedPlace(findMatch);
          return;
        }
        filterClickOnTenant(findMatch, id);
      }
    };
    // Map events - Hovering
    essential.onObjectMouseOut = async ({ targetElement }) => {
      const id = targetElement.vg.id;
      const allMatchingAreService = allTiles
        .filter((item) => item.floorPlanIDs?.includes(id))
        .every((item) => item.type == 'MeetingPlacePageService');
      const originalColor =
        targetElement.vg.originalColor &&
        `#${targetElement.vg.originalColor.toString(16)}`;

      if (
        (checkThatIdIsInValidAndService(id) && allMatchingAreService) ||
        !floorIdsFromContentful.includes(id)
      )
        return;

      const storeIsActive = activeStore?.id === id;

      let color = originalColor || __defaultTileColor;

      if (storeIsActive) {
        color = __activeColor;
      } else if (
        activeTiles.flatMap((tile) => [...(tile.floorPlanIDs ?? []), ...(tile?.unitId ?? [])]).includes(id)
      ) {
        color = __filterColor;
      }
      essential.content.setPlaceColor({
        id,
        color,
      });
    };
    essential.onObjectMouseOver = async ({ targetElement }) => {
      const id = targetElement.vg.id;
      const allMatchingAreService = allTiles
        .filter((item) => item.floorPlanIDs?.includes(id))
        .every((item) => item.type == 'MeetingPlacePageService');
      if (
        (checkThatIdIsInValidAndService(id) && allMatchingAreService) ||
        !floorIdsFromContentful.includes(id)
      )
        return;

      essential.content.setPlaceColor({
        id: id,
        color: __activeColor,
      });
    };
  }

  ///////////////////////
  // METHODS
  //////////////////////
  const goToFloor = async (floorId: string = activeFloorId, zoom = false) => {
    if (zoom) {
      essential?._mapviewer.camera.goTo({
        x: -50,
        y: 0,
        radius: __intialZoomRadius,
      });
    }
    await essential?.venue.goToFloor({
      id: floorId,
      animationDuration: __zoomSpeed,
    });

    setActiveFloorId(() => floorId);

    //If the activeStore have a id of a place which floorId is the same as the floor we are switching to
    if (activeStore?.ids?.length === 2) {
      for (const id of activeStore.ids) {
        if (essential?.content.places[id].floor === floorId) {
          //Go to the Id
          await essential.venue.goToPlace({
            id,
            animationDuration: __zoomSpeed,
            radius: __singleStoreZoomRadius,
          });
          //Set the id to active place
          essential.content.setActivePlace({ place: id });
          return;
        }
      }
    }
  };

  const zoomOut = () => {
    const position = essential?._mapviewer.camera.position as Position;
    position.radius = position.radius + __defaultZoomRadius;
    essential?._mapviewer.camera.goTo(position);
  };

  const zoomIn = () => {
    const position = essential?._mapviewer.camera.position as Position;
    position.radius = position.radius - __defaultZoomRadius;
    essential?._mapviewer.camera.goTo(position);
  };

  const clearAllMarkedPlaces = async (id?: string) => {
    for (const place of allTiles) {
      if ((id && (place.floorPlanIDs?.includes(id) || place.unitId?.includes(id))) || place == undefined)
        continue;
      clearMarkedPlace(place);
    }
  };

  const clearMarkedPlace = async (place: FilterTile) => {
    if (!essential) return;

    const allPlaces = essential.content.places;

    for (const id of [...(place.floorPlanIDs ?? []), ...(place.unitId ?? [])]) {
      if (
        allPlaces[id] == undefined ||
        !Object.keys(allPlaces).includes(id) ||
        !floorIdsFromContentful.includes(id)
      ) {
        continue;
      }

      try {
        const activeTileContentfulId = activeTiles.map(
          (tile) => tile.contentfulId,
        );

        if (
          activeTileContentfulId &&
          activeTileContentfulId.includes(place.contentfulId)
        ) {
          if (checkThatIdIsInValidAndService(id)) {
            setOrResetServiceIcon(id, __filterIcon, true);
          } else {
            const placeIsFilter = activeTiles
              .map((tile) => tile.contentfulId)
              .includes(place.contentfulId);
            essential?.content.setPlaceColor({
              id,
              color: placeIsFilter ? __filterColor : brandGrey2,
            });
          }
        } else {
          if (checkThatIdIsInValidAndService(id)) {
            const icon = place.images?.icon?.url ?? '';
            setOrResetServiceIcon(id, icon);
          } else {
            // essential?.content.setPlaceColor({
            //   id,
            //   color: brandGrey2,
            // });
            essential?.content.resetPlaceColor({ id });
          }
        }
      } catch (error) {
        console.error(`Place: ${place.title}: Throws id for id: ${id}`);
      }
    }
  };

  const centerOnMultipleIds = async (
    sameFloorplaces: MapTile[],
    otherFloorPlaces: MapTile[],
    anotherFloor = false,
  ) => {
    if (!essential) return;

    const newFloor = sameFloorplaces[0].floor as string;

    await goToFloor(newFloor);

    setActiveStore(() => {
      return {
        ...sameFloorplaces[0],
        ids: [
          ...sameFloorplaces.map((place) => place.id),
          ...otherFloorPlaces.map((place) => place.id),
        ],
        hideInfoBubble: true,
      };
    });

    if (anotherFloor) return;

    const points = sameFloorplaces
      .map((place) => place.id)
      .map((id) => essential?.venue.getFootprintPoints({ id })[0]);

    let viewPoint = essential?.venue.getViewpoint({
      points,
    });

    viewPoint = {
      ...viewPoint,
      radius: viewPoint.radius + 200,
      floor: newFloor,
    };

    if (!isMobile && viewPoint.position.lon) {
      viewPoint = {
        ...viewPoint,
        position: {
          ...viewPoint.position,
          lon: viewPoint.position.lon - 0.002,
        },
      };
    }

    await essential?.venue.goToViewpoint({
      ...viewPoint,
      animationDuration: __zoomSpeed,
    });
  };

  const centerOnSingleId = async (
    id: string,
    tile: MapTile,
    sameServiceOnOtherFloors?: string[],
  ) => {
    if (!essential) return;

    const outsideFloor = activeBuilding?.floors.find(
      (floor) => floor.levelIndex == 0,
    )?.id;
    await goToFloor(tile.floor == 'outside' ? outsideFloor : tile.floor);

    await essential.venue.goToPlace({
      id,
      animationDuration: __zoomSpeed,
      radius: __singleStoreZoomRadius,
    });

    const tileIds =
      sameServiceOnOtherFloors && sameServiceOnOtherFloors.length > 0
        ? [...sameServiceOnOtherFloors, tile.id]
        : [tile.id];

    const checkOutsideOnSingleFloor =
      sameServiceOnOtherFloors &&
      sameServiceOnOtherFloors.length > 0 &&
      sameServiceOnOtherFloors.every((id) => {
        if (Object.keys(essential.content.places).includes(id)) {
          return essential.content.places[id].floor === 'outside';
        }
        return false;
      });

    setActiveStore(() => {
      return {
        ...tile,
        ids: tileIds,
        hideInfoBubble: checkOutsideOnSingleFloor,
      };
    });
  };

  const centerOnSingleIdAnotherFloor = async (tile: MapTile) => {
    if (!essential) return;
    // Needs to be in this other when tenant are on another floor

    await goToFloor(tile.floor as string);

    await essential.venue.goToPlace({
      id: tile.id,
      animationDuration: __zoomSpeed,
      radius: __singleStoreZoomRadius,
    });

    setActiveStore(() => {
      return { ...tile, ids: [tile.id] };
    });
  };

  type CalculatedFloors = [MapTile[], MapTile[], MapTile[]];

  const calculateFloorArrays = (tile: FilterTile): CalculatedFloors => {
    if (!essential) return [[], [], []];

    const filteredPlaceIds = Object.keys(essential.content.places).filter(
      (id) => tile.floorPlanIDs?.includes(id) || tile?.unitId?.includes(id),
    );

    const sameFloorPlaces: MapTile[] = [];
    const otherFloorPlaces: MapTile[] = [];

    for (const id of filteredPlaceIds) {
      const currentPlace = essential.content.places[id];
      if (currentPlace.floor === essential.venue.currentFloorID) {
        sameFloorPlaces.push({ ...currentPlace, id });
      } else {
        otherFloorPlaces.push({ ...currentPlace, id });
      }
    }

    return [
      sameFloorPlaces,
      otherFloorPlaces,
      [...sameFloorPlaces, ...otherFloorPlaces],
    ];
  };

  const filterClickOnTenant = async (tile: FilterTile, id?: string) => {
    if (!essential) return;

    const [sameFloorPlaces, otherFloorPlaces, allPlaces] =
      calculateFloorArrays(tile);

    let haveOtherFloorIds: string[] = [];

    if (!id) {
      haveOtherFloorIds = otherFloorPlaces.map((ot) => ot.id);
    }

    if (id) {
      await centerOnSingleId(
        id,
        allPlaces.filter((place) => place.id === id)[0],
      );
    } else if (sameFloorPlaces.length === 1) {
      //If there is only one id located on the current floor, but there are otherplace Ids, center on the one single id and send along the ids for the other floors
      await centerOnSingleId(
        sameFloorPlaces[0].id,
        sameFloorPlaces[0],
        haveOtherFloorIds,
      );
    } else if (sameFloorPlaces.length > 1) {
      await centerOnMultipleIds(sameFloorPlaces, otherFloorPlaces);
    } else if (otherFloorPlaces.length === 1) {
      centerOnSingleIdAnotherFloor(otherFloorPlaces[0]);
    } else if (otherFloorPlaces.length > 1) {
      const filteredSamefloorPlaces = otherFloorPlaces.filter(
        (place) => place.floor === otherFloorPlaces[0].floor,
      );
      const filteredOtherFloorPlaces = otherFloorPlaces.filter(
        (place) => place.floor !== otherFloorPlaces[0].floor,
      );
      if (filteredSamefloorPlaces.length === 1) {
        centerOnSingleIdAnotherFloor(filteredSamefloorPlaces[0]);
      } else if (filteredSamefloorPlaces.length > 0) {
        centerOnMultipleIds(filteredSamefloorPlaces, filteredOtherFloorPlaces);
      }
    }
  };

  const checkFloorOfID = (id: string) => {
    if (!essential) return;
    const allPlaces = essential.content.places;
    if (Object.keys(allPlaces).includes(id) && allPlaces[id] != undefined) {
      return allPlaces[id].floor;
    }
    return false;
  };

  const getLocalizedShortName = (floorId: string) => {
    if (!essential) return;
    return essential?.venue?.getLocalizedShortName({ id: floorId }) || false;
  };

  const closeInformationBubble = async () => {
    if (!activeStore || !essential) return;

    if (isBrowser && searchParams.get('fpActive')) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('fpActive');
      const newUrl = params.toString()
        ? `${pathname}?${params.toString()}`
        : pathname;
      router.replace(newUrl, { scroll: false });
    }

    if (checkThatIdIsInValidAndService(activeStore.id)) {
      const service = serviceData[activeStore.id];
      setOrResetServiceIcon(activeStore.id, service.images?.icon?.url || '');
    } else if (
      Object.keys(essential?.content.places).includes(activeStore.id) &&
      essential.content.places[activeStore.id].icon &&
      typeof essential.content.places[activeStore.id].icon !== 'string'
    ) {
      const placeIsFilter = activeTiles
        .flatMap((tile) => [...(tile.floorPlanIDs ?? []), ...(tile?.unitId ?? [])])
        .includes(activeStore.id);
      const originalColor = essential.content.activePlace?.vg?.originalColor;
      essential?.content.setPlaceColor({
        id: activeStore.id,
        color: placeIsFilter ? __filterColor : originalColor || brandGrey2,
      });
    }
    await goToFloor(activeFloorId, true);

    setActiveStore(() => undefined);
  };

  const setOrResetServiceIcon = (
    id: string,
    iconUrl: string,
    isFilterIcon = false,
  ) => {
    if (!essential) return;
    try {
      const serviceAsPOI = essential._mapviewer.getPOI(id)[0];
      const originalOptions = serviceAsPOI.options();
      serviceAsPOI.options({
        ...originalOptions,
        url: iconUrl,
        zoomScaleFactor: isFilterIcon ? 6 : 0,
        ...(isFilterIcon && { overlay: true }),
      });
    } catch {
      console.error('Failed to set icon for id:', id);
    }
  };

  const resetQueryParams = () => {
    setQueryParams({
      kioskId: '',
      routePreference: '',
      destination: '',
    });
    if (essential) {
      essential.route.clear();
      currentRouteRef?.current?.remove();
      essential?._mapviewer.camera.goTo({
        x: -50,
        y: 0,
        radius: __intialZoomRadius,
      });
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === instruction.length - 1 ? 0 : prevSlide + 1,
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === 0 ? instruction.length - 1 : prevSlide - 1,
    );
  };

  const hasQueryParams =
    queryParams.kioskId ||
    queryParams.routePreference ||
    queryParams.destination;

  /////////////////
  // CONSTANTS
  /////////////////
  const buttonClass = 'w-10 h-10 md:h-12 md:w-12 rounded-full align-middle ';
  return (
    <>
      <div ref={bubbleElement} id="bubble" className="relative z-40">
        {activeStore &&
          Object.keys(activeStore).length > 0 &&
          !activeStore.hideInfoBubble && (
            <FloorPlanInfoBubble
              activeStore={activeStore}
              closeInformationBubble={closeInformationBubble}
              node_locale={node_locale}
              serviceData={serviceData}
            />
          )}
      </div>
      {hasQueryParams && (
        <button
          type="button"
          title="Previous"
          onClick={() => {
            clearUrlParams();
            resetQueryParams();
          }}
          className={
            'absolute left-[20px] top-[40px] h-10 w-10 rounded-full bg-brand-yellow-1 lg:inline'
          }
        >
          <ChevronLeftSmall className="inline-block" />
        </button>
      )}
      <div
        ref={floorPlanMapContainer}
        className="mt-6 h-[calc(100vh_-_180px)] w-full overflow-hidden rounded-t-xl border-t-[1px] border-brand-grey-1 md:h-[calc(100vh_-_180px)] md:rounded-xxl md:border-[1px] lg:h-[80vh]"
      ></div>
      {activeBuilding && Object.keys(activeBuilding).length > 0 && (
        <div
          className={`absolute ${
            isMobile ? 'bottom-[14rem]' : ' bottom-20'
          } right-3 flex-col md:bottom-8 md:right-8 lg:flex`}
        >
          <button
            className={`${buttonClass} mb-4 hidden items-center bg-brand-grey-1 lg:hover:bg-brand-grey-2 lg:flex`}
            onClick={zoomIn}
          >
            <PlusIcon className="mx-auto" />
          </button>
          <button
            className={`${buttonClass} mb-12 hidden items-center bg-brand-grey-1 lg:hover:bg-brand-grey-2 lg:flex`}
            onClick={zoomOut}
          >
            <MinusIcon className="mx-auto" />
          </button>
          <div className="flex flex-col-reverse gap-2 text-headings  md:gap-4">
            {activeBuilding.floors &&
              activeBuilding.floors.map((floor) => {
                const bgColor =
                  floor.id === activeFloorId
                    ? 'bg-brand-yellow-1'
                    : 'bg-brand-grey-1 lg:hover:bg-brand-grey-2';
                return (
                  <button
                    key={floor.id}
                    className={`${buttonClass} ${bgColor}`}
                    onClick={async () => await goToFloor(floor.id)}
                  >
                    {getLocalizedShortName(floor.id)}
                  </button>
                );
              })}
          </div>
        </div>
      )}
      <Script
        src={getVisioWebScriptUrl()}
        onLoad={initVisioWebScript}
        strategy="afterInteractive"
      ></Script>
      {!queryParams.kioskId && (
        <FloorPlanFilter
          homepage={homepage}
          listStyle={listStyle}
          activeTiles={activeTiles}
          performListFiltering={performListFiltering}
          performCategoryListing={performCategoryListing}
          updateFilterList={updateFilterList}
          activeStore={activeStore}
          activeBuilding={activeBuilding}
          setActiveStore={setActiveStore}
          setClickHistory={setClickHistory}
          clickHistory={clickHistory}
          clickOnTenant={filterClickOnTenant}
          clearAllMarkedPlaces={clearAllMarkedPlaces}
          goToFloor={goToFloor}
          checkFloorOfID={checkFloorOfID}
          getLocalizedShortName={getLocalizedShortName}
        />
      )}
      {instruction.length > 0 && queryParams.kioskId != '' && (
        <>
          {!isMobile && (
            <button
              className="prev-button text-white absolute bottom-[50px] left-[29%] z-[100] flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-neutral-grey-100 p-2 text-neutral-grey-400 lg:hover:bg-neutral-grey-300"
              onClick={prevSlide}
            >
              &#10094;
            </button>
          )}
          <Carousel
            className={`absolute z-50 h-[10rem] overflow-hidden rounded-[20px] bg-background-primary shadow-default md:left-[34%] md:w-[40%] ${
              isMobile ? 'bottom-[15px] left-[7%] w-[86%]' : 'bottom-[15px]'
            }`}
            shopHeader={selectedShopRouteDetails?.shopName ?? ''}
            routePreference={selectedShopRouteDetails?.routePreference ?? ''}
            slides={instruction}
            currentSlide={currentSlide}
            nextSlide={nextSlide}
            prevSlide={prevSlide}
          />
          {!isMobile && (
            <button
              className="next-button text-white absolute bottom-[50px] right-[21%] z-[100] flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-neutral-grey-100 p-2 text-neutral-grey-400 lg:hover:bg-neutral-grey-300"
              onClick={nextSlide}
            >
              &#10095;
            </button>
          )}
        </>
      )}
    </>
  );
};
